import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { JerseyModeEnum, LineupPosition, PlayerMedium } from "../../../client/api";
import { AppDispatch } from "../../../store";
import { fetchTotw } from "../../../store/actions/matchActions";
import { makeSelectTotwByLeagueAndDay, selectLanguageOrDefault, selectMatchStatus } from "../../../selectors";
import Loading from "../Loading";
import PlayerImage from "../PlayerImage";
import ScaleFade from "../Animated/ScaleFade";
import StoreMiniBanner from "../StoreMiniBanner";
import { ClavaContext } from "../../../config/contexts";
import { translate } from "../../../config/translator";
import { pathPlayerView } from "../../../config/routes";

interface TOTWProps {
  leagueId: number;
  matchDay: number;
}

type TeamVisual = {
  jerseyColor1: string;
  jerseyColor2: string;
  jerseyMode: JerseyModeEnum;
};

interface TOTWPlayerProps {
  player: PlayerMedium;
  position: LineupPosition;
  team: TeamVisual;
}

const TOTWPlayer: React.FC<TOTWPlayerProps> = ({ player, position, team }) => {
  const { l } = useContext(ClavaContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const H_MARGIN = 0;
  const V_INNER = 5;
  const HALF_HEIGHT = 40;

  const leftFinal = position.x * (100 - 2 * H_MARGIN) + H_MARGIN;
  const INNER_MARGIN_PCT = 12;
  const boundedPercent = Math.min(100 - INNER_MARGIN_PCT, Math.max(INNER_MARGIN_PCT, leftFinal));


  const topBase = position.y * HALF_HEIGHT + V_INNER;
  const topPct = 100 - topBase;
  const adjustedTopPct = ((topPct - 55) / 40) * 100;

  const hasPhoto = !!player.photo;
  const buttonClasses = hasPhoto
    ? "w-16 h-16 overflow-hidden rounded-full cursor-pointer"
    : "w-16 h-16 cursor-pointer";

  return (
    <div
      className="absolute transform -translate-x-1/2 -translate-y-1/2"
      style={{
        left: `${boundedPercent}%`,
        top: `${adjustedTopPct}%`
      }}
    >
      <div className="relative flex flex-col items-center justify-center">
        <div className="relative w-16 h-16">
          <ScaleFade isOpen hoverOnContainer className="w-16 h-16">
            <button 
              type="button" 
              onClick={() => {
                if (player.id) {
                  navigate(pathPlayerView(player.id));
                } else {
                  setOpen(true);
                }
              }} 
              className={buttonClasses}
            >
              <PlayerImage
                image={player.photo ?? undefined}
                width={64}
                className="rounded-full"
                jerseyColor1={team.jerseyColor1}
                jerseyColor2={team.jerseyColor2}
                jerseyMode={team.jerseyMode}
              />
            </button>
          </ScaleFade>
          {player.mainTeam?.emblemUrl && (
            <div className="absolute -bottom-1 -right-1 z-10">
              <PlayerImage
                image={{
                  url: player.mainTeam.emblemUrl,
                  id: `emblem-${player.mainTeam.id}`,
                  uploadDate: new Date().toISOString(),
                  uploadedById: 0,
                  aspectRatio: 1
                }}
                width={24}
                className="rounded-full"
                round
              />
            </div>
          )}
          {open && (
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10">
              <StoreMiniBanner compact noBorder message={translate("downloadUnlimited", l)}
                               onClose={() => setOpen(false)} />
            </div>
          )}
        </div>
        <div className="mt-1 flex flex-col items-center justify-center text-center">
          <span className="text-xs font-semibold">{player.familyName}</span>
          <span className="text-xs">{player.givenName}</span>
        </div>
      </div>
    </div>
  );
};

const TOTW: React.FC<TOTWProps> = ({ leagueId, matchDay }) => {
  const l = useSelector(selectLanguageOrDefault);
  const dispatch = useDispatch<AppDispatch>();
  const selectTotw = useMemo(() => makeSelectTotwByLeagueAndDay(leagueId, matchDay), [leagueId, matchDay]);
  const totwData = useSelector(selectTotw);
  const status = useSelector(selectMatchStatus);

  const totwPlayers = useMemo(() => {
    if (!totwData) return [];

    return totwData.players.map((p) => {
      const primary = p.player.mainTeam?.primaryColor ?? "#ffffff";
      const secondary = p.player.mainTeam?.secondaryColor ?? "#000000";
      const teamVisual: TeamVisual = {
        jerseyColor1: primary,
        jerseyColor2: secondary,
        jerseyMode: JerseyModeEnum.MONO
      };
      return {
        player: p.player,
        position: p.position,
        team: teamVisual
      };
    });
  }, [totwData]);

  useEffect(() => {
    if (!leagueId || !matchDay) return;
    fetchTotw(dispatch, leagueId, matchDay);
  }, [dispatch, leagueId, matchDay, l]);

  if (status === "loading" && !totwData) {
    return <Loading />;
  }

  if (!totwData) {
    return (
      <div className="p-4 text-font-600 dark:text-font-600-dark">
        No TOTW data available for this matchday
      </div>
    );
  }

  return (
    <div className="m-4 relative">
      <div className="relative w-full mx-auto">
        <div className="relative w-full aspect-[345/309] rounded-xl overflow-hidden">
          <div className="absolute inset-0 opacity-60">
            <div className="absolute inset-x-0 bottom-0 h-full">
              <svg className="w-full h-full" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <clipPath id="clipBottomTOTW">
                    <rect x="0" y="0" width="1000" height="1000" rx="40" ry="40" />
                  </clipPath>
                </defs>
                <g clipPath="url(#clipBottomTOTW)">
                  <rect x="0" y="0" width="1000" height="1000" fill="#00AD5A" />
                  <rect x="0" y="50" width="1000" height="100" fill="#00944D" />
                  <rect x="0" y="250" width="1000" height="100" fill="#00944D" />
                  <rect x="0" y="450" width="1000" height="100" fill="#00944D" />
                  <rect x="0" y="650" width="1000" height="100" fill="#00944D" />
                  <rect x="0" y="850" width="1000" height="100" fill="#00944D" />
                  <line x1="20" y1="25" x2="980" y2="25" strokeWidth="3" stroke="#ffffff"
                        vectorEffect="non-scaling-stroke" />
                  <line x1="20" y1="970" x2="980" y2="970" strokeWidth="3" stroke="#ffffff"
                        vectorEffect="non-scaling-stroke" />
                  <line x1="20" y1="25" x2="20" y2="970" strokeWidth="3" stroke="#ffffff"
                        vectorEffect="non-scaling-stroke" />
                  <line x1="980" y1="25" x2="980" y2="970" strokeWidth="3" stroke="#ffffff"
                        vectorEffect="non-scaling-stroke" />
                  <rect x="230" y="680" width="540" height="290" strokeWidth="2" stroke="#ffffff" fill="none"
                        vectorEffect="non-scaling-stroke" />
                  <rect x="340" y="840" width="320" height="130" strokeWidth="2" stroke="#ffffff" fill="none"
                        vectorEffect="non-scaling-stroke" />
                  <path d="M 350 25 A 75 75 0 0 0 650 25" stroke="white" strokeWidth="2" fill="none"
                        vectorEffect="non-scaling-stroke" />
                  <path d="M 650 680 A 55 38 0 0 0 350 680" stroke="white" strokeWidth="2" fill="none"
                        vectorEffect="non-scaling-stroke" />
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {totwPlayers.map((p) => (
        <TOTWPlayer
          player={p.player}
          position={p.position}
          team={p.team}
          key={`totw-player-${p.player.id}-${p.position.id}`}
        />
      ))}
    </div>
  );
};

export default TOTW;
